import { useEffect, useState, useRef, useCallback } from "react";
import {
  Clock, LogIn, LogOut, AlertTriangle, CheckCircle,
  Info, Coffee, Send, ChevronRight, Edit3, Hourglass,
  History, CalendarDays, ChevronLeft, Filter, PlusCircle,
  ClipboardList, XCircle, RefreshCw, LayoutList, LayoutGrid, Umbrella, Trash2, FileCheck,
} from "lucide-react";
import toast from "react-hot-toast";
import { TimesheetReviewTab } from "./clock/TimesheetReviewTab";

// ─── Types ───────────────────────────────────────────────────────────────────

interface ClockBreak {
  id: string;
  breakStartAt: string;
  breakEndAt: string | null;
}

interface ClockEntry {
  id: string;
  teamMemberId: string;
  workDate: string;
  clockInAt: string | null;
  clockOutAt: string | null;
  adjustedClockInAt: string | null;
  adjustedClockOutAt: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  scheduledLunchStart: string | null;
  scheduledLunchDurationMinutes: number | null;
  timezone: string | null;
  flagStatus: string;
  clockInFlag: string | null;
  clockOutFlag: string | null;
  approvalStatus: string;
  overrideReason: string | null;
  notes: string | null;
  breaks: ClockBreak[];
  totalBreakMinutes: number;
  totalWorkedMinutes: number | null;
  totalOvertimeMinutes: number | null;
  pendingAdjustments?: number;
  exclusions?: { id: string; startTime: string; endTime: string; excludedMinutes: number; reason: string }[];
}

interface PendingRequest {
  id: string;
  requestedValue: string;
  requestedAt: string;
}

interface MyAdjustment {
  id: string;
  workDate: string;
  field: string;
  adjustmentType: string;
  requestedValue: string;
  reason: string;
  status: "pending" | "approved" | "rejected";
  requestedAt: string;
  resolvedAt: string | null;
  rejectionReason: string | null;
  timezone?: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const FLAG_LABELS: Record<string, { label: string; color: string }> = {
  pending:         { label: "In Progress",    color: "text-blue-600 bg-blue-50" },
  ok:              { label: "On Time",         color: "text-green-700 bg-green-50" },
  late:            { label: "Late Arrival",    color: "text-yellow-700 bg-yellow-50" },
  early_departure: { label: "Early Departure", color: "text-orange-700 bg-orange-50" },
  overtime:        { label: "Overtime",        color: "text-red-700 bg-red-50" },
  unscheduled:     { label: "Unscheduled Day", color: "text-red-700 bg-red-50" },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DISPLAY_TZ = "America/Los_Angeles";

function fmtTime(iso: string | null, _tz?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "2-digit", minute: "2-digit", timeZone: DISPLAY_TZ,
  });
}

function fmtHHMM(hhmm: string | null, _tz?: string | null): string {
  if (!hhmm) return "—";
  // Use a fixed UTC date to avoid browser-timezone distortion when displaying HH:MM wall-clock times
  const [h, m] = hhmm.split(":").map(Number);
  const date = new Date(Date.UTC(2000, 0, 1, h, m, 0));
  return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" });
}

function fmtElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function fmtMinutes(mins: number | null): string {
  if (mins === null) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtDate(dateStr: string): string {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
}

// Produce a datetime-local string (YYYY-MM-DDTHH:MM) representing `date` in the
// given IANA timezone — so inputs are always pre-filled in the member's timezone,
// regardless of what timezone the browser/AVD is set to.
function toDatetimeLocalInTz(date: Date, tz: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (t: string) => parts.find(p => p.type === t)!.value;
  const h = get("hour");
  return `${get("year")}-${get("month")}-${get("day")}T${h === "24" ? "00" : h}:${get("minute")}`;
}

// Parse a datetime-local input value as a wall-clock time in the given IANA timezone
// and return the corresponding UTC ISO string — the inverse of toDatetimeLocalInTz.
function parseDatetimeLocalInTz(value: string, tz: string): string {
  const refDate = new Date(value + "Z"); // treat value as UTC reference
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).formatToParts(refDate);
  const get = (t: string) => parseInt(parts.find(p => p.type === t)!.value);
  const tzMs = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"), get("second"));
  return new Date(refDate.getTime() + (refDate.getTime() - tzMs)).toISOString();
}

// Short abbreviation to show users which timezone their inputs are interpreted in.
function tzLabel(tz: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
    .formatToParts(new Date())
    .find(p => p.type === "timeZoneName")?.value ?? tz;
}

function toDateInput(date: Date): string {
  return date.toISOString().split("T")[0];
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...init,
  });
  if (res.status === 204) return {} as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data?.message || "Request failed"), data);
  return data as T;
}

// ─── Flag pills ───────────────────────────────────────────────────────────────

function FlagPills({ flags, compact = false }: { flags: string[]; compact?: boolean }) {
  const visible = flags.filter((f) => f !== "ok" && f !== "pending");
  if (compact) {
    if (!visible.length) return <span className="text-xs text-green-600 font-medium">✓</span>;
    return (
      <div className="flex gap-0.5">
        {visible.map((f) => {
          const cfg = FLAG_LABELS[f];
          if (!cfg) return null;
          return (
            <span key={f} title={cfg.label} className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
              <AlertTriangle className="h-2.5 w-2.5" />
            </span>
          );
        })}
      </div>
    );
  }
  if (!visible.length) return <span className="text-xs text-green-600 font-medium">On Time</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((f) => {
        const cfg = FLAG_LABELS[f];
        if (!cfg) return null;
        return (
          <span key={f} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
            <AlertTriangle className="h-3 w-3" />{cfg.label}
          </span>
        );
      })}
    </div>
  );
}

// ─── History tab ──────────────────────────────────────────────────────────────

function RequestsTab({ refreshCounter }: { refreshCounter: number }) {
  const [adjustments, setAdjustments] = useState<MyAdjustment[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest<{ adjustments: MyAdjustment[] }>("/api/v1/clock/my-adjustments");
      setAdjustments(res.adjustments ?? []);
    } catch {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (refreshCounter > 0) void load(); }, [refreshCounter, load]);

  const fmtType = (type: string) => {
    const map: Record<string, string> = {
      missed_clock_in:  "Missed Clock-In",
      missed_clock_out: "Missed Clock-Out",
      incorrect_time:   "Incorrect Time",
      missed_break:     "Missed Break",
      incorrect_break:  "Incorrect Break",
      other:            "Other",
    };
    return map[type] ?? type;
  };

  const fmtField = (field: string) => {
    const map: Record<string, string> = {
      clock_in: "Clock In", clock_out: "Clock Out",
      break_start: "Break Start", break_end: "Break End",
    };
    return map[field] ?? field;
  };

  const statusBadge = (status: string) => {
    if (status === "approved") return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
        <CheckCircle className="h-3 w-3" /> Approved
      </span>
    );
    if (status === "rejected") return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        <XCircle className="h-3 w-3" /> Rejected
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
        <Hourglass className="h-3 w-3" /> Pending
      </span>
    );
  };

  if (loading) return <p className="text-center text-gray-500 py-10 text-sm">Loading requests...</p>;

  if (adjustments.length === 0) return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
      <ClipboardList className="h-10 w-10 text-gray-300 mx-auto mb-3" />
      <p className="text-sm font-medium text-gray-600">No requests yet</p>
      <p className="text-xs text-gray-400 mt-1">Your time adjustment requests will appear here.</p>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{adjustments.length} request{adjustments.length !== 1 ? "s" : ""}</p>
        <button onClick={() => void load()} className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>
      {adjustments.map((adj) => (
        <div key={adj.id} className={`bg-white rounded-xl shadow-sm border p-4 space-y-2 ${
          adj.status === "rejected" ? "border-red-200" :
          adj.status === "approved" ? "border-green-200" : "border-gray-100"
        }`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {new Date(adj.workDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{fmtType(adj.adjustmentType)} — {fmtField(adj.field)}</p>
            </div>
            {statusBadge(adj.status)}
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <p className="text-gray-400 mb-0.5">Requested Time</p>
              <p className="font-semibold text-gray-700">
                {new Date(adj.requestedValue).toLocaleTimeString("en-US", {
                  hour: "2-digit", minute: "2-digit",
                  timeZone: DISPLAY_TZ,
                })}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <p className="text-gray-400 mb-0.5">Submitted</p>
              <p className="font-semibold text-gray-700">
                {new Date(adj.requestedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </p>
            </div>
          </div>

          <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
            <span className="text-gray-400">Reason: </span>{adj.reason}
          </div>

          {adj.status === "rejected" && adj.rejectionReason && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
              <p className="font-semibold mb-0.5">Rejection Reason</p>
              <p>{adj.rejectionReason}</p>
            </div>
          )}

          {adj.status === "approved" && adj.resolvedAt && (
            <p className="text-xs text-green-600">
              Approved on {new Date(adj.resolvedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Time Off tab ─────────────────────────────────────────────────────────────

type TimeOffStatus = "pending" | "approved" | "rejected";
type TimeOffRequestType = "time_off" | "sick" | "personal";

interface MyTimeOffRequest {
  id: string;
  startDate: string;
  endDate: string;
  requestType: TimeOffRequestType;
  reason?: string | null;
  status: TimeOffStatus;
  reviewNote?: string | null;
  createdAt: string;
}

const REQUEST_TYPE_LABELS: Record<TimeOffRequestType, string> = {
  time_off: "Time Off",
  sick: "Sick Day",
  personal: "Personal",
};

function TimeOffTab() {
  const [requests, setRequests] = useState<MyTimeOffRequest[]>([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate]     = useState(today);
  const [endDate, setEndDate]         = useState(today);
  const [requestType, setRequestType] = useState<TimeOffRequestType>("time_off");
  const [reason, setReason]           = useState("");
  const [formError, setFormError]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest<{ requests: MyTimeOffRequest[] }>("/api/v1/time-off/mine");
      setRequests(res.requests ?? []);
    } catch {
      toast.error("Failed to load time-off requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const submit = async () => {
    setFormError(null);
    if (!startDate || !endDate) { setFormError("Start and end dates are required"); return; }
    if (startDate > endDate) { setFormError("End date must be on or after start date"); return; }
    setSubmitting(true);
    try {
      await apiRequest("/api/v1/time-off", {
        method: "POST",
        body: JSON.stringify({ startDate, endDate, requestType, reason: reason.trim() || null }),
      });
      toast.success("Time-off request submitted");
      setShowForm(false);
      setReason("");
      setStartDate(today);
      setEndDate(today);
      void load();
    } catch (err: any) {
      const msg = err?.message ?? "Failed to submit";
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id: string) => {
    setCancelling(id);
    try {
      await apiRequest(`/api/v1/time-off/${id}`, { method: "DELETE" });
      toast.success("Request cancelled");
      void load();
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to cancel");
    } finally {
      setCancelling(null);
    }
  };

  const statusBadge = (status: TimeOffStatus) => {
    if (status === "approved") return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
        <CheckCircle className="h-3 w-3" /> Approved
      </span>
    );
    if (status === "rejected") return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        <XCircle className="h-3 w-3" /> Rejected
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
        <Hourglass className="h-3 w-3" /> Pending
      </span>
    );
  };

  const fmtDateRange = (start: string, end: string) => {
    const s = new Date(start + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
    if (start === end) return s;
    const e = new Date(end + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${s} – ${e}`;
  };

  return (
    <div className="space-y-4">

      {/* Submit request button / collapse toggle */}
      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition"
        >
          <PlusCircle className="h-4 w-4" /> Request Time Off
        </button>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">New Time-Off Request</p>
            <button onClick={() => { setShowForm(false); setFormError(null); }} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Start Date</label>
              <input type="date" value={startDate} min={today} onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-lg border border-gray-200 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">End Date</label>
              <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-lg border border-gray-200 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <select value={requestType} onChange={(e) => setRequestType(e.target.value as TimeOffRequestType)}
              className="w-full rounded-lg border border-gray-200 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="time_off">Time Off</option>
              <option value="sick">Sick Day</option>
              <option value="personal">Personal</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Reason (optional)</label>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Add a note for your manager..."
              className="w-full rounded-lg border border-gray-200 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" />
          </div>

          {formError && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>
          )}

          <button onClick={() => void submit()} disabled={submitting}
            className="w-full py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition">
            {submitting ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      )}

      {/* Refresh */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{requests.length} request{requests.length !== 1 ? "s" : ""}</p>
        <button onClick={() => void load()} className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* List */}
      {loading ? (
        <p className="text-center text-gray-400 text-sm py-8">Loading...</p>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <Umbrella className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600">No requests yet</p>
          <p className="text-xs text-gray-400 mt-1">Submit a request above when you need time off.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <div key={r.id} className={`bg-white rounded-xl shadow-sm border p-4 space-y-2 ${
              r.status === "rejected" ? "border-red-200" :
              r.status === "approved" ? "border-green-200" : "border-gray-100"
            }`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-gray-800">{fmtDateRange(r.startDate, r.endDate)}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{REQUEST_TYPE_LABELS[r.requestType]}</p>
                </div>
                {statusBadge(r.status)}
              </div>

              {r.reason && (
                <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-gray-400">Reason: </span>{r.reason}
                </div>
              )}

              {r.status === "rejected" && r.reviewNote && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
                  <p className="font-semibold mb-0.5">Manager Note</p>
                  <p>{r.reviewNote}</p>
                </div>
              )}

              {r.status === "approved" && r.reviewNote && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-700">
                  <p>{r.reviewNote}</p>
                </div>
              )}

              <p className="text-xs text-gray-400">
                Submitted {new Date(r.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>

              {r.status === "pending" && (
                <button
                  onClick={() => void cancel(r.id)}
                  disabled={cancelling === r.id}
                  className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50 disabled:opacity-50 transition"
                >
                  <Trash2 className="h-3 w-3" />
                  {cancelling === r.id ? "Cancelling..." : "Cancel Request"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── My Schedule tab ──────────────────────────────────────────────────────────

interface MyAssignment {
  id: string;
  customerId: string;
  customerName: string | null;
  assignmentDate: string;
  startsAt: string;
  endsAt: string;
  sourceTimezone: string;
  isPrimary: boolean;
}

interface MySchedulePeriod {
  id: string;
  weekStart: string;
  version: number;
  status: string;
}

function toMondayPortal(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  const dow = d.getUTCDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

function addDaysPortal(dateStr: string, n: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function fmtScheduleDate(iso: string): string {
  return new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
}

function fmtScheduleTime(iso: string, tz: string): string {
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz });
  } catch {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  }
}

function MyScheduleTab() {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [weekStart, setWeekStart] = useState(() => toMondayPortal(todayStr));
  const [period, setPeriod] = useState<MySchedulePeriod | null>(null);
  const [assignments, setAssignments] = useState<MyAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiRequest<{ period: MySchedulePeriod | null; assignments: MyAssignment[] }>(`/api/v1/schedule/mine?weekStart=${weekStart}`)
      .then(d => { setPeriod(d.period); setAssignments(d.assignments); })
      .catch(() => { setPeriod(null); setAssignments([]); })
      .finally(() => setLoading(false));
  }, [weekStart]);

  const sundayStr = addDaysPortal(weekStart, 6);
  const weekLabel = `${new Date(weekStart + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })} – ${new Date(sundayStr + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}`;

  return (
    <div className="space-y-4">
      {/* Week nav */}
      <div className="flex items-center justify-between">
        <button onClick={() => setWeekStart(w => addDaysPortal(w, -7))} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="h-4 w-4 text-gray-600" />
        </button>
        <div className="text-center">
          <p className="text-sm font-medium text-gray-800">{weekLabel}</p>
          {period && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${period.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {period.status === "published" ? "Published" : period.status}
            </span>
          )}
        </div>
        <button onClick={() => setWeekStart(w => addDaysPortal(w, 7))} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronRight className="h-4 w-4 text-gray-600" />
        </button>
      </div>

      {loading ? (
        <p className="text-center text-gray-400 py-8 text-sm">Loading…</p>
      ) : !period ? (
        <div className="text-center py-10 text-gray-400">
          <CalendarDays className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No published schedule for this week</p>
        </div>
      ) : assignments.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <CalendarDays className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No assignments for this week</p>
        </div>
      ) : (
        <ScheduleByDay assignments={assignments} />
      )}
    </div>
  );
}

function ScheduleByDay({ assignments }: { assignments: MyAssignment[] }) {
  // Group into ordered day sections — assignments already arrive sorted by
  // date/time from the API, so grouping just needs to preserve that order.
  const days: { date: string; entries: MyAssignment[] }[] = [];
  for (const a of assignments) {
    const last = days[days.length - 1];
    if (last && last.date === a.assignmentDate) last.entries.push(a);
    else days.push({ date: a.assignmentDate, entries: [a] });
  }

  return (
    <div className="space-y-3">
      {days.map(({ date, entries }) => (
        <div key={date}>
          <div className="flex items-center justify-between px-1 mb-1.5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{fmtScheduleDate(date)}</p>
            {entries.length > 1 && (
              <span className="text-xs text-gray-400">{entries.length} assignments</span>
            )}
          </div>
          <div className="bg-white rounded-xl border divide-y divide-gray-100 overflow-hidden">
            {entries.map(a => (
              <div key={a.id} className="flex items-center gap-2 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-800 text-sm truncate">{a.customerName ?? "Account"}</p>
                  <p className="text-xs text-gray-500">
                    {fmtScheduleTime(a.startsAt, a.sourceTimezone)} – {fmtScheduleTime(a.endsAt, a.sourceTimezone)}
                    <span className="text-gray-400 ml-1">({a.sourceTimezone.replace("America/", "")})</span>
                  </p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${a.isPrimary ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                  {a.isPrimary ? "Primary" : "Backup"}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function HistoryTab({ onRequestAdjustment, onAddRecord, refreshCounter }: {
  onRequestAdjustment: (entry: ClockEntry) => void;
  onAddRecord: () => void;
  refreshCounter: number;
}) {
  const [entries, setEntries]       = useState<ClockEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);

  // Filters
  const today = toDateInput(new Date());
  const thirtyDaysAgo = toDateInput(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [startDate, setStartDate]   = useState(thirtyDaysAgo);
  const [endDate, setEndDate]       = useState(today);
  const [flagFilter, setFlagFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<"card" | "list">("card");

  const load = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: "20" });
      if (startDate) params.set("startDate", startDate);
      if (endDate)   params.set("endDate", endDate);
      if (flagFilter !== "all") params.set("flagStatus", flagFilter);

      const res = await apiRequest<{ entries: ClockEntry[]; pagination: Pagination }>(
        `/api/v1/clock/my-history?${params.toString()}`
      );
      setEntries(res.entries);
      setPagination(res.pagination);
    } catch {
      toast.error("Failed to load attendance history");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, flagFilter]);

  useEffect(() => {
    setPage(1);
    void load(1);
  }, [load]);

  // Reload when parent signals a successful adjustment submission
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => {
    if (refreshCounter > 0) {
      setPage(1);
      void loadRef.current(1);
    }
  }, [refreshCounter]);

  const handlePageChange = (p: number) => {
    setPage(p);
    void load(p);
  };

  return (
    <div className="space-y-4">

      {/* Header row: filters + view toggle + add record */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-gray-500">Your attendance history</p>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setViewMode("card")}
              className={`px-2 py-1.5 transition ${viewMode === "card" ? "bg-blue-600 text-white" : "bg-white text-gray-400 hover:text-gray-600"}`}
              title="Card view"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`px-2 py-1.5 transition ${viewMode === "list" ? "bg-blue-600 text-white" : "bg-white text-gray-400 hover:text-gray-600"}`}
              title="List view"
            >
              <LayoutList className="h-3.5 w-3.5" />
            </button>
          </div>
          <button
            onClick={onAddRecord}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition"
          >
            <PlusCircle className="h-3.5 w-3.5" /> Add Time Record
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
        <button
          onClick={() => setShowFilters((v) => !v)}
          className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-800"
        >
          <Filter className="h-4 w-4" />
          Filters
          <ChevronRight className={`h-4 w-4 transition-transform ${showFilters ? "rotate-90" : ""}`} />
        </button>

        {showFilters && (
          <div className="grid grid-cols-1 gap-3 pt-1">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Exception</label>
              <select
                value={flagFilter}
                onChange={(e) => setFlagFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-200 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="all">All</option>
                <option value="ok">On Time</option>
                <option value="late">Late Arrival</option>
                <option value="early_departure">Early Departure</option>
                <option value="overtime">Overtime</option>
                <option value="unscheduled">Unscheduled</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">Loading...</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">No attendance records found.</div>
      ) : viewMode === "card" ? (
        <div className="space-y-2">
          {entries.map((e) => {
            const effectiveIn  = e.adjustedClockInAt  ?? e.clockInAt;
            const effectiveOut = e.adjustedClockOutAt ?? e.clockOutAt;
            const flags = [e.clockInFlag, e.clockOutFlag].filter(Boolean) as string[];

            return (
              <div key={e.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-blue-500 shrink-0" />
                    <span className="text-sm font-semibold text-gray-800">{fmtDate(e.workDate)}</span>
                  </div>
                  {(e.pendingAdjustments ?? 0) > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
                      <Hourglass className="h-3 w-3" /> {e.pendingAdjustments} Pending Review
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <p className="text-gray-400 mb-0.5">Clock In</p>
                    <p className="font-semibold text-gray-800">{fmtTime(effectiveIn, e.timezone)}</p>
                    {e.adjustedClockInAt && e.clockInAt && (
                      <p className="text-gray-400 mt-0.5">Orig: {fmtTime(e.clockInAt, e.timezone)}</p>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <p className="text-gray-400 mb-0.5">Clock Out</p>
                    <p className="font-semibold text-gray-800">{fmtTime(effectiveOut, e.timezone)}</p>
                    {e.adjustedClockOutAt && e.clockOutAt && (
                      <p className="text-gray-400 mt-0.5">Orig: {fmtTime(e.clockOutAt, e.timezone)}</p>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2.5">
                    <p className="text-gray-400 mb-0.5">Worked</p>
                    <p className="font-semibold text-gray-800">{fmtMinutes(e.totalWorkedMinutes)}</p>
                    {(e.totalOvertimeMinutes ?? 0) > 0 && (
                      <p className="text-red-500 mt-0.5">+{fmtMinutes(e.totalOvertimeMinutes)} OT</p>
                    )}
                  </div>
                </div>
                {(e.scheduledStart || e.scheduledEnd) && (
                  <div className="text-xs text-gray-500 flex gap-3">
                    {e.scheduledStart && <span>Sched: {fmtHHMM(e.scheduledStart, e.timezone)}{e.scheduledEnd ? ` – ${fmtHHMM(e.scheduledEnd, e.timezone)}` : ""}</span>}
                    {e.totalBreakMinutes > 0 && <span>Break: {fmtMinutes(e.totalBreakMinutes)}</span>}
                  </div>
                )}
                <FlagPills flags={flags} />
                {(e.exclusions ?? []).length > 0 && (
                  <div className="space-y-1">
                    {(e.exclusions ?? []).map((x) => (
                      <div key={x.id} className="flex items-start gap-1.5 px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 text-xs text-orange-700">
                        <span className="shrink-0 font-semibold">−{x.excludedMinutes}m excluded</span>
                        <span className="text-orange-500">·</span>
                        <span className="text-orange-600">{x.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(e.pendingAdjustments ?? 0) > 0 ? (
                  <div className="w-full px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-700 flex items-center gap-1.5">
                    <Hourglass className="h-3.5 w-3.5 shrink-0" />
                    Adjustment request pending manager review — cannot submit another until resolved.
                  </div>
                ) : (
                  <button
                    onClick={() => onRequestAdjustment(e)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 transition"
                  >
                    <span className="flex items-center gap-1.5"><Edit3 className="h-3.5 w-3.5" /> Request Adjustment</span>
                    <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ── List view ── */
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="divide-y divide-gray-100">
            {entries.map((e) => {
              const effectiveIn  = e.adjustedClockInAt  ?? e.clockInAt;
              const effectiveOut = e.adjustedClockOutAt ?? e.clockOutAt;
              const flags = [e.clockInFlag, e.clockOutFlag].filter(Boolean) as string[];
              const hasPending = (e.pendingAdjustments ?? 0) > 0;

              return (
                <div key={e.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition">
                  {/* Date */}
                  <div className="w-20 shrink-0">
                    <p className="text-xs font-semibold text-gray-800 leading-tight">
                      {new Date(e.workDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(e.workDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" })}
                    </p>
                  </div>

                  {/* In / Out */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-xs text-gray-700">
                      <span className="font-medium">{fmtTime(effectiveIn, e.timezone) ?? "—"}</span>
                      <span className="text-gray-300">→</span>
                      <span className="font-medium">{fmtTime(effectiveOut, e.timezone) ?? "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-400">{fmtMinutes(e.totalWorkedMinutes)}</span>
                      {e.totalBreakMinutes > 0 && <span className="text-xs text-gray-400">· {fmtMinutes(e.totalBreakMinutes)} break</span>}
                      {(e.totalOvertimeMinutes ?? 0) > 0 && <span className="text-xs text-red-500">+{fmtMinutes(e.totalOvertimeMinutes)} OT</span>}
                    </div>
                  </div>

                  {/* Status + action */}
                  <div className="flex items-center gap-2 shrink-0">
                    <FlagPills flags={flags} compact />
                    {hasPending ? (
                      <span className="inline-flex items-center gap-0.5 text-yellow-600" title="Adjustment pending review">
                        <Hourglass className="h-3.5 w-3.5" />
                      </span>
                    ) : (
                      <button
                        onClick={() => onRequestAdjustment(e)}
                        title="Request adjustment"
                        className="text-gray-300 hover:text-blue-500 transition"
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 disabled:opacity-40 hover:bg-gray-50"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Prev
          </button>
          <span className="text-xs text-gray-400">Page {page} of {pagination.totalPages} · {pagination.total} records</span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= pagination.totalPages}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 disabled:opacity-40 hover:bg-gray-50"
          >
            Next <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type Tab = "today" | "history" | "requests" | "time-off" | "my-schedule" | "timesheet-review";
type ModalMode = "approval" | "override" | "adjustment" | "add-record" | null;

export function ClockPortal() {
  // A timesheet review email link lands here as /clock?tab=timesheet-review&periodId=... — pick
  // up the deep link once on mount (a click still has to happen on the tab itself; the link only
  // gets the agent to the right screen).
  const initialDeepLink = (() => {
    const params = new URLSearchParams(window.location.search);
    const urlTab = params.get("tab");
    return urlTab === "timesheet-review"
      ? { tab: "timesheet-review" as Tab, periodId: params.get("periodId") }
      : { tab: "today" as Tab, periodId: null };
  })();

  const [tab, setTab] = useState<Tab>(initialDeepLink.tab);
  const [reviewPeriodId] = useState<string | null>(initialDeepLink.periodId);
  const [requestEditsBanner, setRequestEditsBanner] = useState<string | null>(null);
  // The timesheet review period the PCC is currently requesting corrections for, if any --
  // threaded into every adjustment submitted while this banner is showing so the Team Lead
  // actually sees it linked to the right period, instead of it silently going nowhere.
  const [requestEditsPeriodId, setRequestEditsPeriodId] = useState<string | null>(null);
  // Badge count for the Timesheet tab button itself — separate from Layout.tsx's own sidebar
  // badge poll, since this needs to render on the tab bar regardless of which tab is active.
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  useEffect(() => {
    apiRequest<{ data: unknown[] }>("/api/v1/timesheet-review/pending")
      .then((res) => setPendingReviewCount(res.data.length))
      .catch(() => {});
  }, []);

  const [entry, setEntry]       = useState<ClockEntry | null>(null);
  const [loading, setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [pendingClockIn, setPendingClockIn]   = useState<PendingRequest | null>(null);
  const [pendingClockOut, setPendingClockOut] = useState<PendingRequest | null>(null);
  const [pendingStaleClockOutAdj, setPendingStaleClockOutAdj] = useState<PendingRequest | null>(null);
  const [todayOverride, setTodayOverride] = useState<{ type: string; isWorkingDay: boolean; notes?: string | null } | null>(null);

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const [breakElapsed, setBreakElapsed] = useState(0);
  const breakTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [modalMode, setModalMode]             = useState<ModalMode>(null);
  const [pendingAction, setPendingAction]     = useState<"in" | "out" | null>(null);
  const [approvalMessage, setApprovalMessage] = useState("");
  const [selectedTime, setSelectedTime]       = useState(""); // for approval/override modals
  const [overrideReason, setOverrideReason]   = useState("");
  const [notes, setNotes]                     = useState("");

  // Manual time selection in Today tab (employee can override before clocking in/out)
  const [useCustomTime, setUseCustomTime]   = useState(false);
  const [customClockTime, setCustomClockTime] = useState("");

  // Adjustment — may be for today's entry OR a historical entry
  const [adjEntry, setAdjEntry]         = useState<ClockEntry | null>(null);
  const [adjType, setAdjType]           = useState("incorrect_time");
  const [adjField, setAdjField]         = useState("clock_in");
  const [adjValue, setAdjValue]         = useState("");
  const [adjOutageEnd, setAdjOutageEnd] = useState("");
  const [adjBreakMinutes, setAdjBreakMinutes] = useState("");
  const [adjReason, setAdjReason]       = useState("");
  const [submittingAdj, setSubmittingAdj] = useState(false);

  // Add Time Record (missed full day)
  const [addRecordDate, setAddRecordDate]     = useState("");
  const [addRecordIn, setAddRecordIn]         = useState("");
  const [addRecordOut, setAddRecordOut]       = useState("");
  const [addRecordBreakMins, setAddRecordBreakMins] = useState<number | "">(30);
  const [addRecordReason, setAddRecordReason] = useState("");
  const [submittingRecord, setSubmittingRecord] = useState(false);

  // Triggers HistoryTab to reload after a successful adjustment / add-record submission
  const [historyRefresh, setHistoryRefresh] = useState(0);

  // Second session (e.g. training / shift coverage after main shift is complete)
  const [startingNewSession, setStartingNewSession] = useState(false);
  const [newSessionReason, setNewSessionReason] = useState("");
  const [newSessionReasonOther, setNewSessionReasonOther] = useState("");

  const loadToday = async () => {
    try {
      const res = await apiRequest<{
        entry: ClockEntry | null;
        pendingClockInRequest: PendingRequest | null;
        pendingClockOutRequest: PendingRequest | null;
        pendingStaleClockOutAdj: PendingRequest | null;
        todayOverride: { type: string; isWorkingDay: boolean; notes?: string | null } | null;
      }>("/api/v1/clock/today");
      setEntry(res.entry);
      setPendingClockIn(res.pendingClockInRequest ?? null);
      setPendingClockOut(res.pendingClockOutRequest ?? null);
      setPendingStaleClockOutAdj(res.pendingStaleClockOutAdj ?? null);
      setTodayOverride(res.todayOverride ?? null);
    } catch {
      toast.error("Failed to load clock status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadToday(); }, []);

  useEffect(() => {
    const activeBreak = entry?.breaks?.find((b) => !b.breakEndAt);
    if (activeBreak) {
      const start = new Date(activeBreak.breakStartAt).getTime();
      const tick = () => setBreakElapsed(Math.floor((Date.now() - start) / 1000));
      tick();
      breakTimerRef.current = setInterval(tick, 1000);
    } else {
      if (breakTimerRef.current) clearInterval(breakTimerRef.current);
      setBreakElapsed(0);
    }
    return () => { if (breakTimerRef.current) clearInterval(breakTimerRef.current); };
  }, [entry]);

  const handleClockInResponse = (err: unknown) => {
    const error = err as { message?: string; requiresOverride?: boolean; requiresApproval?: boolean };
    if (error?.requiresApproval) {
      setApprovalMessage(error.message || "This clock-in requires manager approval.");
      setPendingAction("in");
      setSelectedTime(toDatetimeLocalInTz(new Date(), entry?.timezone ?? DISPLAY_TZ));
      setModalMode("approval");
    } else if (error?.requiresOverride) {
      setPendingAction("in");
      setModalMode("override");
    } else {
      toast.error(error?.message || "Failed to clock in");
    }
  };

  const handleClockOutResponse = (err: unknown) => {
    const error = err as { message?: string; requiresApproval?: boolean };
    if (error?.requiresApproval) {
      setApprovalMessage(error.message || "This clock-out requires manager approval.");
      setPendingAction("out");
      setSelectedTime(toDatetimeLocalInTz(new Date(), entry?.timezone ?? DISPLAY_TZ));
      setModalMode("approval");
    } else {
      toast.error((err as Error)?.message || "Failed to clock out");
    }
  };

  const clockIn = async (opts: { overrideReason?: string; notes?: string; sendForApproval?: boolean; selectedTime?: string } = {}, onSuccess?: () => void) => {
    setSubmitting(true);
    try {
      const res = await apiRequest<{ entry?: ClockEntry; pendingApproval?: boolean }>(
        "/api/v1/clock/in", { method: "POST", body: JSON.stringify(opts) }
      );
      if (res.pendingApproval) {
        await loadToday();
        toast.success("Clock-in request sent to manager for approval");
        closeModal();
        onSuccess?.();
      } else if (res.entry) {
        setEntry(res.entry);
        setPendingClockIn(null);
        toast.success("Clocked in successfully");
        closeModal();
        resetCustomTime();
        onSuccess?.();
      }
    } catch (err) { handleClockInResponse(err); }
    finally { setSubmitting(false); }
  };

  const clockOut = async (opts: { overrideReason?: string; notes?: string; sendForApproval?: boolean; selectedTime?: string } = {}) => {
    setSubmitting(true);
    try {
      const res = await apiRequest<{ entry?: ClockEntry; pendingApproval?: boolean }>(
        "/api/v1/clock/out", { method: "POST", body: JSON.stringify(opts) }
      );
      if (res.pendingApproval) {
        await loadToday();
        toast.success("Clock-out request sent to manager for approval");
        closeModal();
      } else if (res.entry) {
        setEntry(res.entry);
        setPendingClockOut(null);
        toast.success("Clocked out successfully");
        closeModal();
        resetCustomTime();
      }
    } catch (err) { handleClockOutResponse(err); }
    finally { setSubmitting(false); }
  };

  const startBreak = async () => {
    setSubmitting(true);
    try {
      const res = await apiRequest<{ entry: ClockEntry }>("/api/v1/clock/break/start", { method: "POST" });
      setEntry(res.entry);
      toast.success("Break started");
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed to start break"); }
    finally { setSubmitting(false); }
  };

  const endBreak = async () => {
    setSubmitting(true);
    try {
      const res = await apiRequest<{ entry: ClockEntry }>("/api/v1/clock/break/end", { method: "POST" });
      setEntry(res.entry);
      toast.success("Break ended");
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed to end break"); }
    finally { setSubmitting(false); }
  };

  const sendForApproval = async () => {
    const isoTime = selectedTime ? parseDatetimeLocalInTz(selectedTime, entry?.timezone ?? DISPLAY_TZ) : undefined;
    if (pendingAction === "in")  await clockIn({ sendForApproval: true, notes: notes || undefined, selectedTime: isoTime });
    else if (pendingAction === "out") await clockOut({ sendForApproval: true, notes: notes || undefined, selectedTime: isoTime });
  };

  const submitOverride = async () => {
    if (!overrideReason.trim()) { toast.error("Reason is required"); return; }
    if (pendingAction === "in")  await clockIn({ overrideReason: overrideReason.trim(), notes: notes || undefined });
    else if (pendingAction === "out") await clockOut({ overrideReason: overrideReason.trim(), notes: notes || undefined });
  };

  const openAdjustmentFor = (e: ClockEntry) => {
    setAdjEntry(e);
    setAdjType("incorrect_time");
    setAdjField("clock_in");
    setAdjValue("");
    setAdjOutageEnd("");
    setAdjBreakMinutes("");
    setAdjReason("");
    setModalMode("adjustment");
  };

  const submitAdjustment = async () => {
    const isOutage = adjType === "system_outage";
    const isBreak  = adjType === "incorrect_break";
    if (isOutage) {
      if (!adjValue || !adjOutageEnd || !adjReason.trim()) {
        toast.error("Outage start time, end time, and reason are all required");
        return;
      }
      const tz = adjEntry?.timezone ?? entry?.timezone ?? DISPLAY_TZ;
      const start = new Date(parseDatetimeLocalInTz(adjValue, tz));
      const end   = new Date(parseDatetimeLocalInTz(adjOutageEnd, tz));
      if (end <= start) { toast.error("Outage end time must be after start time"); return; }
    } else if (isBreak) {
      const mins = parseInt(adjBreakMinutes, 10);
      if (!adjBreakMinutes || isNaN(mins) || mins < 0) { toast.error("Please enter a valid break duration in minutes"); return; }
      if (!adjReason.trim()) { toast.error("Reason is required"); return; }
    } else {
      if (!adjValue || !adjReason.trim()) { toast.error("Corrected time and reason are required"); return; }
    }
    setSubmittingAdj(true);
    const tz = adjEntry?.timezone ?? entry?.timezone ?? DISPLAY_TZ;
    try {
      const mins = isBreak ? parseInt(adjBreakMinutes, 10) : 0;
      await apiRequest("/api/v1/clock/adjustments", {
        method: "POST",
        body: JSON.stringify({
          clockEntryId:      adjEntry?.id ?? null,
          workDate:          adjEntry?.workDate ?? now.toISOString().split("T")[0],
          field:             isOutage || isBreak ? undefined : adjField,
          adjustmentType:    adjType,
          requestedValue:    isBreak ? new Date(mins * 60 * 1000).toISOString() : parseDatetimeLocalInTz(adjValue, tz),
          ...(isOutage && adjOutageEnd ? { endRequestedValue: parseDatetimeLocalInTz(adjOutageEnd, tz) } : {}),
          reason:            adjReason.trim(),
          ...(requestEditsPeriodId ? { timesheetReviewPeriodId: requestEditsPeriodId } : {}),
        }),
      });
      toast.success(adjType === "system_outage"
        ? "System outage recorded — exclusion applied to your hours"
        : "Adjustment request submitted — pending manager approval");
      setHistoryRefresh((n) => n + 1);
      void loadToday();
      closeModal();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to submit adjustment");
    } finally { setSubmittingAdj(false); }
  };

  const submitAddRecord = async () => {
    if (!addRecordDate || !addRecordIn || !addRecordOut) {
      toast.error("Date, clock-in time, and clock-out time are all required");
      return;
    }
    if (!addRecordReason.trim()) { toast.error("Reason is required"); return; }
    const memberTz = entry?.timezone ?? DISPLAY_TZ;
    const inISO  = parseDatetimeLocalInTz(addRecordIn, memberTz);
    const outISO = parseDatetimeLocalInTz(addRecordOut, memberTz);
    if (new Date(outISO) <= new Date(inISO)) {
      toast.error("Clock-out must be after clock-in");
      return;
    }
    setSubmittingRecord(true);
    try {
      await apiRequest("/api/v1/clock/adjustments/missed-day", {
        method: "POST",
        body: JSON.stringify({
          workDate:     addRecordDate,
          clockIn:      inISO,
          clockOut:     outISO,
          breakMinutes: addRecordBreakMins !== "" ? Number(addRecordBreakMins) : 0,
          reason:       addRecordReason.trim(),
        }),
      });
      toast.success("Missing day request submitted — pending manager approval");
      setHistoryRefresh((n) => n + 1);
      closeModal();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to submit request");
    } finally { setSubmittingRecord(false); }
  };

  const closeModal = () => {
    setModalMode(null);
    setPendingAction(null);
    setOverrideReason("");
    setNotes("");
    setSelectedTime("");
    setAdjReason("");
    setAdjValue("");
    setAdjOutageEnd("");
    setAdjBreakMinutes("");
    setAdjEntry(null);
    setAddRecordDate("");
    setAddRecordIn("");
    setAddRecordOut("");
    setAddRecordBreakMins(30);
    setAddRecordReason("");
  };

  // The ISO time to send with every clock-in/clock-out
  const getClockTime = (): string | undefined => {
    if (!useCustomTime || !customClockTime) return undefined;
    return parseDatetimeLocalInTz(customClockTime, entry?.timezone ?? DISPLAY_TZ);
  };

  const resetCustomTime = () => {
    setUseCustomTime(false);
    setCustomClockTime("");
  };

  const todayDate = entry?.timezone
    ? new Date().toLocaleDateString("en-CA", { timeZone: entry.timezone })
    : now.toISOString().split("T")[0];
  const effectiveClockIn  = entry?.adjustedClockInAt  ?? entry?.clockInAt;
  const effectiveClockOut = entry?.adjustedClockOutAt ?? entry?.clockOutAt;

  // Open entry from a previous day — employee forgot to clock out.
  // Night-shift grace: if the shift crosses midnight (scheduledEnd < scheduledStart)
  // and the current wall-clock time in the employee's timezone hasn't passed the
  // scheduled end yet, the shift is still active — don't flag as a missed clock-out.
  const isStaleShift = (() => {
    if (!entry || effectiveClockOut) return false;
    if (entry.workDate >= todayDate) return false;
    if (entry.scheduledEnd && entry.scheduledStart && entry.scheduledEnd < entry.scheduledStart) {
      const tz = entry.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
      const nowHHMM = now.toLocaleTimeString("en-CA", {
        timeZone: tz, hour12: false, hour: "2-digit", minute: "2-digit",
      }).slice(0, 5);
      if (nowHHMM < entry.scheduledEnd) return false;
      // Past the scheduled end, but only just — running a few minutes (or hours) long is
      // normal (wrapping up a call, finishing notes). Give the same grace window the backend
      // itself treats as a legitimate shift length before calling it stale, instead of cutting
      // off the instant the clock ticks past the scheduled end minute.
      const clockInIso = entry.adjustedClockInAt ?? entry.clockInAt;
      if (clockInIso) {
        const hoursOpen = (now.getTime() - new Date(clockInIso).getTime()) / 3_600_000;
        if (hoursOpen < 16) return false;
      }
    } else if (!entry.scheduledStart || !entry.scheduledEnd) {
      // Unscheduled shift (e.g. an overridden off-hours clock-in) has no scheduled end to
      // compare against, so the calendar day rolling over can't tell us whether the shift
      // is still genuinely active. Fall back to a max reasonable shift length (matches the
      // backend's own MAX_SHIFT_DURATION_HOURS) instead of flagging stale at midnight —
      // otherwise an in-progress overnight shift gets wrongly treated as forgotten.
      const clockInIso = entry.adjustedClockInAt ?? entry.clockInAt;
      if (clockInIso) {
        const hoursOpen = (now.getTime() - new Date(clockInIso).getTime()) / 3_600_000;
        if (hoursOpen < 16) return false;
      }
    }
    return true;
  })();
  const isClockedIn  = Boolean(entry && !effectiveClockOut && !isStaleShift);
  const onBreak      = Boolean(isClockedIn && entry?.breaks?.some((b) => !b.breakEndAt));
  const activeBreak  = isClockedIn ? entry?.breaks?.find((b) => !b.breakEndAt) : undefined;
  const isComplete   = Boolean(effectiveClockOut);
  const hasPendingIn  = Boolean(pendingClockIn && !entry);
  const hasPendingOut = Boolean(pendingClockOut && (isClockedIn || isStaleShift));
  const clockInFlag  = entry?.clockInFlag  ?? entry?.flagStatus ?? null;
  const clockOutFlag = entry?.clockOutFlag ?? null;
  const flags = [clockInFlag, clockOutFlag].filter(Boolean) as string[];

  const lunchEnd = (() => {
    if (!entry?.scheduledLunchStart || !entry?.scheduledLunchDurationMinutes) return null;
    const [h, m] = entry.scheduledLunchStart.split(":").map(Number);
    const d = new Date();
    d.setHours(h, m + entry.scheduledLunchDurationMinutes, 0, 0);
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: entry.timezone || undefined });
  })();

  return (
    <div className="min-h-[80vh] flex flex-col items-center px-4 py-8">

      {/* Live clock */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-2 text-blue-600 mb-1">
          <Clock className="h-5 w-5" />
          <span className="text-2xl font-bold tabular-nums">
            {now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: DISPLAY_TZ })}
          </span>
        </div>
        <p className="text-gray-500 text-sm">
          {now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: DISPLAY_TZ })}
        </p>
      </div>

      {/* Tabs */}
      <div className="w-full max-w-xl mb-4">
        <div className="flex bg-gray-100 rounded-xl p-1 gap-0.5">
          {([
            { id: "today",             icon: Clock,         label: "Today"      },
            { id: "history",           icon: History,       label: "History"    },
            { id: "requests",          icon: ClipboardList, label: "Requests"   },
            { id: "time-off",          icon: Umbrella,      label: "Time Off"   },
            { id: "my-schedule",       icon: CalendarDays,  label: "My Schedule" },
            { id: "timesheet-review",  icon: FileCheck,     label: "Timesheet"  },
          ] as { id: Tab; icon: React.ElementType; label: string }[]).map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => {
                // Leaving History after requesting edits — stop linking further adjustments to
                // that period, since the PCC is done with this correction session.
                if (tab === "history" && id !== "history" && requestEditsPeriodId) {
                  setRequestEditsBanner(null);
                  setRequestEditsPeriodId(null);
                }
                setTab(id);
              }}
              className={`relative flex-1 flex flex-col items-center justify-center py-1.5 rounded-lg text-xs font-medium transition gap-0.5 ${
                tab === id ? "bg-white shadow text-blue-600" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="relative">
                <Icon className="h-3.5 w-3.5" />
                {id === "timesheet-review" && pendingReviewCount > 0 && (
                  <span className="absolute -top-1 -right-1.5 h-2 w-2 rounded-full bg-red-500" />
                )}
              </span>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="w-full max-w-xl">

        {/* ── Today tab ─────────────────────────────────────────────────── */}
        {tab === "today" && (
          loading ? (
            <p className="text-center text-gray-500 py-10">Loading...</p>
          ) : (
            <div className="space-y-4">

              {/* Status card */}
              <div className="bg-white rounded-2xl shadow-md p-6 space-y-4">

                {/* Status pill */}
                <div className="flex justify-center">
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                    isStaleShift  ? "bg-red-50 text-red-700"
                      : hasPendingIn  ? "bg-yellow-50 text-yellow-700"
                      : hasPendingOut ? "bg-yellow-50 text-yellow-700"
                      : onBreak ? "bg-orange-50 text-orange-700"
                      : isClockedIn ? "bg-green-50 text-green-700"
                      : isComplete ? "bg-gray-100 text-gray-600"
                      : "bg-gray-100 text-gray-500"
                  }`}>
                    {isStaleShift ? <><AlertTriangle className="h-4 w-4" /> Shift Not Closed</>
                      : hasPendingIn  ? <><Hourglass className="h-4 w-4" /> Clock-In Pending Approval</>
                      : hasPendingOut ? <><Hourglass className="h-4 w-4" /> Clock-Out Pending Approval</>
                      : onBreak ? <><Coffee className="h-4 w-4" /> On Break</>
                      : isClockedIn ? <><CheckCircle className="h-4 w-4" /> Clocked In</>
                      : isComplete ? <><CheckCircle className="h-4 w-4" /> Shift Complete</>
                      : <><Info className="h-4 w-4" /> Not Clocked In</>}
                  </span>
                </div>

                {/* Pending clock-in notice */}
                {hasPendingIn && pendingClockIn && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-3 text-xs text-yellow-800 space-y-1">
                    <div className="flex items-center gap-1.5 font-semibold">
                      <Hourglass className="h-3.5 w-3.5" /> Early Clock-In Request — Awaiting Approval
                    </div>
                    <div className="flex justify-between text-yellow-700">
                      <span>Requested Time</span>
                      <span className="font-medium">{fmtTime(pendingClockIn.requestedValue, null)}</span>
                    </div>
                    <div className="flex justify-between text-yellow-600">
                      <span>Submitted At</span>
                      <span>{fmtTime(pendingClockIn.requestedAt, null)}</span>
                    </div>
                    <p className="text-yellow-700 pt-0.5">You can still clock in at your scheduled time using the button below.</p>
                  </div>
                )}

                {/* Pending clock-out notice */}
                {hasPendingOut && pendingClockOut && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-3 text-xs text-yellow-800 space-y-1">
                    <div className="flex items-center gap-1.5 font-semibold">
                      <Hourglass className="h-3.5 w-3.5" /> Clock-Out Request — Awaiting Manager Approval
                    </div>
                    <div className="flex justify-between text-yellow-700">
                      <span>Requested Time</span>
                      <span className="font-medium">{fmtTime(pendingClockOut.requestedValue, null)}</span>
                    </div>
                    <div className="flex justify-between text-yellow-600">
                      <span>Submitted At</span>
                      <span>{fmtTime(pendingClockOut.requestedAt, null)}</span>
                    </div>
                  </div>
                )}

                {/* Time grid */}
                {entry && (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-1">Clock In</p>
                      <p className="font-semibold text-gray-800">{fmtTime(effectiveClockIn ?? null, entry.timezone)}</p>
                      {entry.adjustedClockInAt && entry.clockInAt && (
                        <p className="text-xs text-gray-400 mt-0.5">Original: {fmtTime(entry.clockInAt, entry.timezone)}</p>
                      )}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-1">Clock Out</p>
                      <p className="font-semibold text-gray-800">{fmtTime(effectiveClockOut ?? null, entry.timezone)}</p>
                      {entry.adjustedClockOutAt && entry.clockOutAt && (
                        <p className="text-xs text-gray-400 mt-0.5">Original: {fmtTime(entry.clockOutAt, entry.timezone)}</p>
                      )}
                    </div>
                    {entry.scheduledStart && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-1">Scheduled Start</p>
                        <p className="font-semibold text-gray-800">{fmtHHMM(entry.scheduledStart, entry.timezone)}</p>
                      </div>
                    )}
                    {entry.scheduledEnd && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-1">Scheduled End</p>
                        <p className="font-semibold text-gray-800">{fmtHHMM(entry.scheduledEnd, entry.timezone)}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Lunch break section */}
                {entry?.scheduledLunchStart && (
                  <div className="border border-orange-100 rounded-lg p-3 text-sm space-y-1">
                    <p className="text-xs font-medium text-orange-600 mb-1.5">Lunch Break</p>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>Scheduled</span>
                      <span className="font-medium">{fmtHHMM(entry.scheduledLunchStart, entry.timezone)}{lunchEnd ? ` – ${lunchEnd}` : ""}</span>
                    </div>
                    {onBreak && activeBreak && (
                      <>
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>Break Started</span>
                          <span className="font-medium">{fmtTime(activeBreak.breakStartAt, entry.timezone)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-orange-700 font-semibold">
                          <span>Elapsed</span>
                          <span className="tabular-nums">{fmtElapsed(breakElapsed)}</span>
                        </div>
                      </>
                    )}
                    {entry.totalBreakMinutes > 0 && !onBreak && (
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Total Break</span>
                        <span className="font-medium">{fmtMinutes(entry.totalBreakMinutes)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Exception flags */}
                {flags.filter((f) => f !== "ok" && f !== "pending").length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {flags.filter((f) => f !== "ok" && f !== "pending").map((f) => {
                      const cfg = FLAG_LABELS[f];
                      if (!cfg) return null;
                      return (
                        <span key={f} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                          <AlertTriangle className="h-3 w-3" />{cfg.label}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* End-of-day summary */}
                {isComplete && (
                  <div className="border-t border-gray-100 pt-4 space-y-2 text-sm">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Shift Summary</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex justify-between col-span-2 bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-gray-500">Total Worked</span>
                        <span className="font-semibold text-gray-800">{fmtMinutes(entry?.totalWorkedMinutes ?? null)}</span>
                      </div>
                      {(entry?.totalOvertimeMinutes ?? 0) > 0 && (
                        <div className="flex justify-between col-span-2 bg-red-50 rounded-lg px-3 py-2">
                          <span className="text-red-500">Overtime</span>
                          <span className="font-semibold text-red-700">{fmtMinutes(entry?.totalOvertimeMinutes ?? null)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Schedule override banner — leave / day off / holiday */}
              {todayOverride && !todayOverride.isWorkingDay && !entry && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">
                      {todayOverride.type === "leave" && "You are on approved leave today"}
                      {todayOverride.type === "day_off" && "You have a day off today"}
                      {todayOverride.type === "holiday" && "Today is a company holiday"}
                    </p>
                    {todayOverride.notes && (
                      <p className="text-xs text-amber-700 mt-0.5">{todayOverride.notes}</p>
                    )}
                    <p className="text-xs text-amber-600 mt-1">Clock-in is not available. Contact your manager if this is incorrect.</p>
                  </div>
                </div>
              )}

              {/* New session — start a second session after main shift is complete */}
              {isComplete && !startingNewSession && (
                <button
                  onClick={() => { setStartingNewSession(true); setNewSessionReason(""); setNewSessionReasonOther(""); resetCustomTime(); }}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition"
                >
                  <LogIn className="h-4 w-4" /> Start New Session
                </button>
              )}

              {isComplete && startingNewSession && (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-700">
                    Your main shift is complete. This will log a separate session for today (e.g. training or shift coverage).
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-gray-600">Reason for additional session</label>
                    <select
                      value={newSessionReason}
                      onChange={(e) => setNewSessionReason(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 text-sm px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="">Select a reason...</option>
                      <option value="HIPAA Training">HIPAA Training</option>
                      <option value="Shift Coverage">Shift Coverage</option>
                      <option value="Other">Other</option>
                    </select>
                    {newSessionReason === "Other" && (
                      <input
                        type="text"
                        value={newSessionReasonOther}
                        onChange={(e) => setNewSessionReasonOther(e.target.value)}
                        placeholder="Describe the reason..."
                        className="w-full rounded-xl border border-gray-200 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                    )}
                  </div>

                  <div className="bg-gray-50 rounded-xl px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 font-medium">Clock-In Time</span>
                      <button
                        onClick={() => { if (useCustomTime) { resetCustomTime(); } else { setCustomClockTime(toDatetimeLocalInTz(new Date(), entry?.timezone ?? DISPLAY_TZ)); setUseCustomTime(true); } }}
                        className={`text-xs font-medium px-2.5 py-1 rounded-lg transition ${useCustomTime ? "bg-blue-100 text-blue-700 hover:bg-blue-200" : "bg-white border border-gray-200 text-gray-500 hover:bg-gray-100"}`}
                      >
                        {useCustomTime ? "Using custom time" : "Current time"}
                      </button>
                    </div>
                    {useCustomTime ? (
                      <input
                        type="datetime-local"
                        value={customClockTime}
                        onChange={(e) => setCustomClockTime(e.target.value)}
                        className="w-full rounded-lg border border-blue-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                      />
                    ) : (
                      <p className="text-sm font-semibold text-gray-700 tabular-nums">
                        {now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: DISPLAY_TZ })}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      const reason = newSessionReason === "Other" ? newSessionReasonOther.trim() : newSessionReason;
                      if (!reason) { toast.error("Please select a reason for the additional session"); return; }
                      // If clocking in after scheduled shift end, send directly for manager approval
                      const isPastScheduledEnd = (() => {
                        if (!entry?.scheduledEnd) return false;
                        const [h, m] = entry.scheduledEnd.split(":").map(Number);
                        const end = new Date();
                        end.setHours(h, m, 0, 0);
                        return new Date() > end;
                      })();
                      void clockIn(
                        { overrideReason: reason, selectedTime: getClockTime(), sendForApproval: isPastScheduledEnd || undefined },
                        () => { setStartingNewSession(false); setNewSessionReason(""); setNewSessionReasonOther(""); }
                      );
                    }}
                    disabled={submitting}
                    className="w-full py-4 rounded-2xl bg-green-500 hover:bg-green-600 text-white font-semibold text-base flex items-center justify-center gap-2 shadow-md transition disabled:opacity-50"
                  >
                    <LogIn className="h-5 w-5" />
                    {submitting ? "Please wait..." : "Clock In"}
                  </button>

                  <button
                    onClick={() => { setStartingNewSession(false); setNewSessionReason(""); setNewSessionReasonOther(""); resetCustomTime(); }}
                    className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-500 text-sm hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Stale shift warning — previous day still open */}
              {isStaleShift && entry && (
                <div className={`border rounded-xl px-4 py-4 space-y-3 ${pendingStaleClockOutAdj ? "bg-yellow-50 border-yellow-200" : "bg-red-50 border-red-200"}`}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${pendingStaleClockOutAdj ? "text-yellow-500" : "text-red-500"}`} />
                    <div className="text-sm">
                      <p className={`font-semibold mb-0.5 ${pendingStaleClockOutAdj ? "text-yellow-800" : "text-red-800"}`}>
                        {pendingStaleClockOutAdj ? "Clock-Out Pending Approval" : "You forgot to clock out"}
                      </p>
                      <p className={`text-xs ${pendingStaleClockOutAdj ? "text-yellow-700" : "text-red-600"}`}>
                        Your {new Date(entry.workDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} shift is still open.{" "}
                        {pendingStaleClockOutAdj
                          ? "Your clock-out request is awaiting manager approval. You can clock in for today below."
                          : "Your previous shift is still open. Submit a clock-out time so your manager can approve the correct hours. You can still clock in for today below."}
                      </p>
                    </div>
                  </div>

                  {/* Submitted adjustment details */}
                  {pendingStaleClockOutAdj && (
                    <div className="flex justify-between items-center text-xs text-yellow-700 bg-white border border-yellow-200 rounded-lg px-3 py-2">
                      <span className="flex items-center gap-1.5"><Hourglass className="h-3 w-3" /> Requested clock-out</span>
                      <span className="font-semibold">
                        {new Date(pendingStaleClockOutAdj.requestedValue).toLocaleTimeString("en-US", {
                          hour: "2-digit", minute: "2-digit",
                          timeZone: entry.timezone || undefined,
                        })}
                      </span>
                    </div>
                  )}

                  {/* Submit button — only when no pending adjustment */}
                  {!pendingStaleClockOutAdj && (
                    <button
                      onClick={() => {
                        setAdjEntry(entry);
                        setAdjType("missed_clock_out");
                        setAdjField("clock_out");
                        setAdjValue("");
                        setAdjReason("");
                        setModalMode("adjustment");
                      }}
                      className="w-full py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold flex items-center justify-center gap-2 transition"
                    >
                      <Edit3 className="h-4 w-4" /> Submit Clock-Out Time
                    </button>
                  )}
                </div>
              )}

              {/* Start/End Break — independent of the pending-clock-out-approval gate below.
                  A pending overtime approval only blocks re-clocking-out, not taking a break;
                  the backend has no restriction on breaks in that state either. */}
              {isClockedIn && (
                <button
                  onClick={onBreak ? endBreak : startBreak}
                  disabled={submitting}
                  className={`w-full py-3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 shadow transition disabled:opacity-50 ${
                    onBreak ? "bg-orange-500 hover:bg-orange-600 text-white"
                      : "bg-white border border-orange-300 text-orange-600 hover:bg-orange-50"
                  }`}
                >
                  <Coffee className="h-4 w-4" />
                  {submitting ? "Please wait..." : onBreak ? "End Break" : "Start Break"}
                </button>
              )}

              {/* Action buttons — always shown when stale (backend allows clock-in regardless of pending adj); hidden only for today's pending clock-out */}
              {!isComplete && (!hasPendingOut || isStaleShift) && !(todayOverride && !todayOverride.isWorkingDay && !entry) && (
                <div className="space-y-2">
                  {/* Optional time picker — employee can select a different clock-in/out time */}
                  {!onBreak && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 font-medium">
                          {isClockedIn ? "Clock-Out Time" : "Clock-In Time"}
                        </span>
                        <button
                          onClick={() => {
                            if (useCustomTime) {
                              resetCustomTime();
                            } else {
                              setCustomClockTime(toDatetimeLocalInTz(new Date(), entry?.timezone ?? DISPLAY_TZ));
                              setUseCustomTime(true);
                            }
                          }}
                          className={`text-xs font-medium px-2.5 py-1 rounded-lg transition ${
                            useCustomTime
                              ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                              : "bg-white border border-gray-200 text-gray-500 hover:bg-gray-100"
                          }`}
                        >
                          {useCustomTime ? "Using custom time" : "Current time"}
                        </button>
                      </div>
                      {useCustomTime ? (
                        <input
                          type="datetime-local"
                          value={customClockTime}
                          onChange={(e) => setCustomClockTime(e.target.value)}
                          className="w-full rounded-lg border border-blue-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                        />
                      ) : (
                        <p className="text-sm font-semibold text-gray-700 tabular-nums">
                          {now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: DISPLAY_TZ })}
                        </p>
                      )}
                    </div>
                  )}

                  <button
                    onClick={() => isClockedIn ? clockOut({ selectedTime: getClockTime() }) : clockIn({ selectedTime: getClockTime() })}
                    disabled={submitting || onBreak}
                    className={`w-full py-4 rounded-2xl text-white font-semibold text-base flex items-center justify-center gap-2 shadow-md transition disabled:opacity-50 ${
                      isClockedIn ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"
                    }`}
                  >
                    {isClockedIn ? <LogOut className="h-5 w-5" /> : <LogIn className="h-5 w-5" />}
                    {submitting ? "Please wait..." : isClockedIn ? "Clock Out" : "Clock In"}
                  </button>
                  {onBreak && <p className="text-xs text-center text-gray-400">End your break before clocking out</p>}
                </div>
              )}

              {!isStaleShift && hasPendingOut && (
                <div className="text-center text-xs text-gray-400 py-2">
                  Your clock-out request is with your manager. Button will unlock once approved.
                </div>
              )}

              {/* Request adjustment for today */}
              {entry && !isStaleShift && (
                (entry.pendingAdjustments ?? 0) > 0 ? (
                  <div className="w-full px-4 py-3 rounded-xl bg-yellow-50 border border-yellow-200 text-sm text-yellow-700 flex items-center gap-2">
                    <Hourglass className="h-4 w-4 shrink-0" />
                    Adjustment request pending manager review — cannot submit another until resolved.
                  </div>
                ) : (
                  <button
                    onClick={() => openAdjustmentFor(entry)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 transition"
                  >
                    <span className="flex items-center gap-2"><Edit3 className="h-4 w-4" /> Request Time Adjustment</span>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </button>
                )
              )}
            </div>
          )
        )}

        {/* ── History tab ───────────────────────────────────────────────── */}
        {tab === "history" && (
          <>
            {requestEditsBanner && (
              <div className="mb-3 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
                Click <strong>"Request Adjustment"</strong> next to each day within <strong>{requestEditsBanner}</strong> below
                that needs a correction — each day is requested separately. If you don't see those dates listed, adjust the date
                range filter above.
              </div>
            )}
            <HistoryTab
              onRequestAdjustment={openAdjustmentFor}
              onAddRecord={() => setModalMode("add-record")}
              refreshCounter={historyRefresh}
            />
          </>
        )}

        {/* ── Requests tab ──────────────────────────────────────────────── */}
        {tab === "requests" && (
          <RequestsTab refreshCounter={historyRefresh} />
        )}

        {/* ── Time Off tab ───────────────────────────────────────────────── */}
        {tab === "time-off" && <TimeOffTab />}
        {tab === "my-schedule" && <MyScheduleTab />}

        {/* ── Timesheet Review tab ─────────────────────────────────────────
            Four-times-a-month self-confirmation of logged hours (see plan doc). Confirming
            or requesting edits happens in-portal, authenticated — the email is only a deep
            link into this tab, never a direct action. */}
        {tab === "timesheet-review" && (
          <TimesheetReviewTab
            periodIdFromUrl={reviewPeriodId}
            onRequestEdits={(periodId, periodLabel) => {
              setRequestEditsBanner(periodLabel);
              setRequestEditsPeriodId(periodId);
              setTab("history");
            }}
            onPendingCountChange={setPendingReviewCount}
          />
        )}
      </div>

      {/* ── Approval popup ────────────────────────────────────────────────────── */}
      {modalMode === "approval" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-5 w-5" />
              <h2 className="text-base font-semibold">Manager Approval Required</h2>
            </div>
            <p className="text-sm text-gray-600">{approvalMessage}</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {pendingAction === "in" ? "Clock-In" : "Clock-Out"} Time
              </label>
              <input type="datetime-local" value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-yellow-400" />
              <p className="text-xs text-gray-400 mt-1">Pre-filled with current time. Adjust if needed.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="Add a note for your manager..." />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={closeModal} className="flex-1 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">Go Back</button>
              <button onClick={sendForApproval} disabled={submitting}
                className="flex-1 py-2 rounded-lg bg-yellow-500 text-white text-sm font-semibold hover:bg-yellow-600 disabled:opacity-50 flex items-center justify-center gap-1.5">
                <Send className="h-3.5 w-3.5" />
                {submitting ? "Submitting..." : "Send for Approval"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Override popup ────────────────────────────────────────────────────── */}
      {modalMode === "override" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              <h2 className="text-base font-semibold">Override Required</h2>
            </div>
            <p className="text-sm text-gray-600">Today is not a scheduled work day. Please provide a reason to clock in.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
              <textarea rows={3} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                placeholder="Enter reason..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                placeholder="Optional notes..." />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={closeModal} className="flex-1 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={submitOverride} disabled={submitting}
                className="flex-1 py-2 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50">
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Adjustment modal ──────────────────────────────────────────────────── */}
      {modalMode === "adjustment" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center gap-2 text-blue-600">
              <Edit3 className="h-5 w-5" />
              <h2 className="text-base font-semibold">Request Time Adjustment</h2>
            </div>
            {adjEntry && (
              <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                For shift: <span className="font-medium">{fmtDate(adjEntry.workDate)}</span>
              </p>
            )}
            <p className="text-xs text-gray-400">Your original recorded time is always preserved. Corrections require manager approval.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adjustment Type</label>
              <select value={adjType} onChange={(e) => { setAdjType(e.target.value); setAdjValue(""); setAdjOutageEnd(""); setAdjBreakMinutes(""); }}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400">
                <option value="missed_clock_in">Missed Clock-In</option>
                <option value="missed_clock_out">Missed Clock-Out</option>
                <option value="incorrect_time">Incorrect Time</option>
                <option value="incorrect_break">Incorrect Break Duration</option>
                <option value="system_outage">System Outage</option>
                <option value="other">Other</option>
              </select>
            </div>
            {adjType !== "system_outage" && adjType !== "incorrect_break" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Which Field</label>
              <select value={adjField} onChange={(e) => setAdjField(e.target.value)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400">
                <option value="clock_in">Clock In</option>
                <option value="clock_out">Clock Out</option>
                <option value="break_start">Break Start</option>
                <option value="break_end">Break End</option>
              </select>
            </div>
            )}
            {adjType === "system_outage" ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Outage Start <span className="text-red-500">*</span>
                    <span className="ml-1 text-xs text-gray-400 font-normal">({tzLabel(adjEntry?.timezone ?? entry?.timezone ?? DISPLAY_TZ)})</span>
                  </label>
                  <input type="datetime-local" value={adjValue} onChange={(e) => setAdjValue(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Outage End <span className="text-red-500">*</span>
                    <span className="ml-1 text-xs text-gray-400 font-normal">({tzLabel(adjEntry?.timezone ?? entry?.timezone ?? DISPLAY_TZ)})</span>
                  </label>
                  <input type="datetime-local" value={adjOutageEnd} onChange={(e) => setAdjOutageEnd(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
                <p className="text-xs text-gray-400">Minutes in this window will be deducted from your worked hours pending manager approval.</p>
              </>
            ) : adjType === "incorrect_break" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correct Break Duration <span className="text-red-500">*</span>
                <span className="ml-1 text-xs text-gray-400 font-normal">(minutes)</span>
              </label>
              <input type="number" min="0" max="480" value={adjBreakMinutes} onChange={(e) => setAdjBreakMinutes(e.target.value)}
                placeholder="e.g. 30"
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Corrected Time <span className="text-red-500">*</span>
                <span className="ml-1 text-xs text-gray-400 font-normal">({tzLabel(adjEntry?.timezone ?? entry?.timezone ?? DISPLAY_TZ)})</span>
              </label>
              <input type="datetime-local" value={adjValue} onChange={(e) => setAdjValue(e.target.value)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
              <textarea rows={3} value={adjReason} onChange={(e) => setAdjReason(e.target.value)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="e.g. Internet outage, forgot to clock in..." />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={closeModal} className="flex-1 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={submitAdjustment} disabled={submittingAdj}
                className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                {submittingAdj ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Time Record modal ─────────────────────────────────────── */}
      {modalMode === "add-record" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center gap-2 text-blue-600">
              <PlusCircle className="h-5 w-5" />
              <h2 className="text-base font-semibold">Add Time Record</h2>
            </div>
            <p className="text-xs text-gray-400">For a day you forgot to clock in and out. Your manager will review and approve.</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={addRecordDate}
                onChange={(e) => setAddRecordDate(e.target.value)}
                max={new Date(Date.now() - 86400000).toISOString().slice(0, 10)}
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clock In <span className="text-red-500">*</span>
                  <span className="ml-1 text-xs text-gray-400 font-normal">({tzLabel(entry?.timezone ?? DISPLAY_TZ)})</span>
                </label>
                <input
                  type="datetime-local"
                  value={addRecordIn}
                  onChange={(e) => setAddRecordIn(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clock Out <span className="text-red-500">*</span>
                  <span className="ml-1 text-xs text-gray-400 font-normal">({tzLabel(entry?.timezone ?? DISPLAY_TZ)})</span>
                </label>
                <input
                  type="datetime-local"
                  value={addRecordOut}
                  onChange={(e) => setAddRecordOut(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Break Duration</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="720"
                  value={addRecordBreakMins}
                  onChange={(e) => setAddRecordBreakMins(e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="0"
                  className="w-24 rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <span className="text-sm text-gray-500">minutes</span>
                <span className="text-xs text-gray-400">(optional)</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
              <textarea
                rows={3}
                value={addRecordReason}
                onChange={(e) => setAddRecordReason(e.target.value)}
                placeholder="e.g. System was down, forgot to clock in..."
                className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={closeModal} className="flex-1 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => void submitAddRecord()} disabled={submittingRecord}
                className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                {submittingRecord ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
